import { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Chip, Textarea } from '@nextui-org/react';
import { Icon } from '@iconify/react/offline';
import AttachIcon from '@iconify-icons/tabler/paperclip';
import type {
  CaseCommandResult,
  ShipmentCase,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import useUserStore from 'common/src/stores/useUserStore';
import { Alert } from '../../components/ui';
import { useCaseCommand } from './use-case-command';

export default function CaseNoteComposer({
  record,
  identity,
  onSaved,
  onLockedChange,
}: {
  record: ShipmentCase;
  identity: string;
  onSaved: (result: CaseCommandResult) => void;
  onLockedChange: (locked: boolean) => void;
}) {
  const { t } = useTranslation('shipmentCases');
  const { userProfile } = useUserStore();
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const command = useCaseCommand(record, identity, (result) => {
    setNote('');
    setFiles([]);
    if (fileInput.current) fileInput.current.value = '';
    onSaved(result);
  });
  const frozen = command.frozen || command.busy;
  useEffect(() => {
    onLockedChange(frozen);
    return () => onLockedChange(false);
  }, [frozen, onLockedChange]);
  const author =
    [userProfile?.first_name, userProfile?.last_name]
      .filter(Boolean)
      .join(' ') || t('Usuario');
  return (
    <form
      className='flex items-start gap-3'
      aria-label={t('Agregar comentario o documento')}
      onSubmit={(e) => {
        e.preventDefault();
        if (note.trim() || files.length || frozen)
          void command.save(
            {
              action: 'note',
              case_id: record.id,
              version: record.version,
              note,
            },
            files
          );
      }}
    >
      <Avatar
        name={author}
        getInitials={(name) =>
          name
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => Array.from(part)[0])
            .join('')
            .toUpperCase()
        }
        size='sm'
        className='mt-1 h-7 w-7 shrink-0 text-[11px]'
      />
      <div className='flex min-w-0 flex-1 flex-col gap-2 rounded-medium border border-default-200 bg-content2/30 p-3'>
        <Textarea
          aria-label={t('Comentario')}
          placeholder={t('Escribe un comentario…')}
          value={note}
          onValueChange={setNote}
          minRows={2}
          maxRows={6}
          maxLength={8000}
          isDisabled={frozen}
          variant='flat'
          classNames={{
            inputWrapper: 'bg-transparent shadow-none p-0',
            input: 'text-[13px]',
          }}
        />
        {files.length > 0 && (
          <div className='flex flex-wrap gap-2'>
            {files.map((file, i) => (
              <Chip
                key={i}
                size='sm'
                className='max-w-full'
                classNames={{ content: 'truncate' }}
                onClose={
                  frozen
                    ? undefined
                    : () =>
                        setFiles((current) =>
                          current.filter((_, index) => index !== i)
                        )
                }
              >
                {file.name}
              </Chip>
            ))}
          </div>
        )}
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <input
            ref={fileInput}
            type='file'
            multiple
            hidden
            aria-label={t('Documentos (máximo 10, hasta 10 MB cada uno)')}
            accept='.pdf,.png,.jpg,.jpeg,.webp,.txt,.eml,.doc,.docx,.xls,.xlsx'
            disabled={frozen}
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          <Button
            size='sm'
            variant='light'
            startContent={<Icon icon={AttachIcon} />}
            isDisabled={frozen}
            onPress={() => fileInput.current?.click()}
          >
            {t('Adjuntar documento')}
          </Button>
          <Button
            size='sm'
            color='primary'
            type='submit'
            isLoading={command.busy}
            isDisabled={!note.trim() && !files.length && !frozen}
          >
            {t(frozen ? 'Reintentar envío' : 'Guardar')}
          </Button>
        </div>
        {command.error && <Alert variant='danger'>{command.error}</Alert>}
        {frozen && !command.busy && (
          <p className='text-xs text-warning'>
            {t(
              'El envío está fijado para reintentar sin duplicar la acción. Conserva esta ventana abierta hasta completarlo.'
            )}
          </p>
        )}
      </div>
    </form>
  );
}
